import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeftRight, FileText, PackageCheck, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { downloadPdf } from '../utils/downloadPdf';
import { fmtDateTime, todayLocalISO } from '../lib/datetime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const userCan = (user, module, action = 'ver') => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  if (user.permissions) return user.permissions[module]?.[action] === true;
  return false;
};

const STATUS_BADGE = {
  in_transit: { label: 'En tránsito', className: 'bg-amber-100 text-amber-800' },
  received: { label: 'Recibida', className: 'bg-green-100 text-green-800' },
  cancelled: { label: 'Cancelada', className: 'bg-gray-200 text-gray-600' },
};

export default function WarehouseTransfers() {
  const { user } = useOutletContext();
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('in_transit');
  const [receiveTarget, setReceiveTarget] = useState(null);
  const [receiveBy, setReceiveBy] = useState('');
  const [receiveDate, setReceiveDate] = useState('');
  const [receiveNotes, setReceiveNotes] = useState('');
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);

  const canReceive = userCan(user, 'transferencias_deposito', 'recibir');
  const canSend = userCan(user, 'transferencias_deposito', 'enviar');

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    if (receiveTarget) {
      setReceiveBy(user?.name || '');
      setReceiveDate(todayLocalISO());
      setReceiveNotes('');
    }
  }, [receiveTarget, user]);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/warehouse-transfers`, { withCredentials: true });
      setTransfers(res.data);
    } catch (e) {
      toast.error('Error al cargar transferencias');
    } finally {
      setLoading(false);
    }
  };

  const handleReceive = async () => {
    if (!receiveBy.trim()) { toast.error('Indicá quién recibió'); return; }
    setBusy(true);
    try {
      await axios.post(`${BACKEND_URL}/api/warehouse-transfers/${receiveTarget.transfer_id}/receive`, {
        received_by_name: receiveBy.trim(),
        received_date: receiveDate || null,
        reception_notes: receiveNotes.trim() || null,
      }, { withCredentials: true });
      toast.success('Transferencia recibida. Stock ingresado al depósito destino.');
      setReceiveTarget(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo recibir la transferencia');
    } finally { setBusy(false); }
  };

  const handleCancel = async () => {
    if (cancelReason.trim().length < 5) { toast.error('Indicá el motivo (mínimo 5 caracteres)'); return; }
    setBusy(true);
    try {
      await axios.post(`${BACKEND_URL}/api/warehouse-transfers/${cancelTarget.transfer_id}/cancel`, { reason: cancelReason.trim() }, { withCredentials: true });
      toast.success('Transferencia cancelada. Stock devuelto al origen.');
      setCancelTarget(null); setCancelReason('');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cancelar');
    } finally { setBusy(false); }
  };

  const filtered = transfers.filter(t => t.status === tab);

  const renderTable = (rows) => (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Comprobante</TableHead>
            <TableHead>Origen → Destino</TableHead>
            <TableHead className="text-center">Artículos</TableHead>
            <TableHead>Enviada</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead className="text-right">Acciones</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Sin transferencias</TableCell></TableRow>
          ) : rows.map(t => {
            const badge = STATUS_BADGE[t.status] || { label: t.status, className: '' };
            return (
              <TableRow key={t.transfer_id} data-testid={`transfer-row-${t.transfer_id}`}>
                <TableCell className="font-mono text-sm font-medium">{t.transfer_number}</TableCell>
                <TableCell className="text-sm">{t.source_warehouse_name} <ArrowLeftRight className="inline w-3 h-3 mx-1" /> {t.dest_warehouse_name}</TableCell>
                <TableCell className="text-center">{t.items?.length || 0}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{fmtDateTime(t.created_at)}<br />{t.created_by_name}</TableCell>
                <TableCell><Badge className={badge.className}>{badge.label}</Badge></TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-1 justify-end">
                    <Button variant="ghost" size="icon" title="Ver detalle" onClick={() => setDetail(t)} data-testid={`transfer-detail-${t.transfer_id}`}>
                      <PackageCheck className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" title="Remito PDF" onClick={() => downloadPdf(`/warehouse-transfers/${t.transfer_id}/pdf`, t.transfer_number)} data-testid={`transfer-pdf-${t.transfer_id}`}>
                      <FileText className="w-4 h-4" />
                    </Button>
                    {t.status === 'in_transit' && canReceive && (
                      <Button size="sm" className="h-8" onClick={() => setReceiveTarget(t)} data-testid={`transfer-receive-${t.transfer_id}`}>
                        Recibir
                      </Button>
                    )}
                    {t.status === 'in_transit' && canSend && (
                      <Button variant="outline" size="icon" title="Cancelar" className="h-8 w-8" onClick={() => setCancelTarget(t)} data-testid={`transfer-cancel-${t.transfer_id}`}>
                        <X className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;

  const pendingCount = transfers.filter(t => t.status === 'in_transit').length;

  return (
    <div className="space-y-6" data-testid="warehouse-transfers-page">
      <div className="flex items-center gap-3">
        <ArrowLeftRight className="w-7 h-7 text-blue-600" />
        <div>
          <h1 className="text-2xl font-bold">Transferencias entre Depósitos</h1>
          <p className="text-sm text-muted-foreground">Envíos entre depósitos con recepción en destino</p>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Transferencias</CardTitle></CardHeader>
        <CardContent>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="in_transit" data-testid="tab-in-transit">En tránsito {pendingCount > 0 && <Badge className="ml-2 bg-amber-500 text-white">{pendingCount}</Badge>}</TabsTrigger>
              <TabsTrigger value="received" data-testid="tab-received">Recibidas</TabsTrigger>
              <TabsTrigger value="cancelled" data-testid="tab-cancelled">Canceladas</TabsTrigger>
            </TabsList>
            <TabsContent value="in_transit" className="mt-4">{renderTable(filtered)}</TabsContent>
            <TabsContent value="received" className="mt-4">{renderTable(filtered)}</TabsContent>
            <TabsContent value="cancelled" className="mt-4">{renderTable(filtered)}</TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Confirmar recepción */}
      <Dialog open={!!receiveTarget} onOpenChange={(o) => { if (!o) setReceiveTarget(null); }}>
        <DialogContent data-testid="receive-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><PackageCheck className="w-5 h-5 text-green-600" /> Confirmar recepción</DialogTitle>
            <DialogDescription>
              Vas a recibir la transferencia <strong>{receiveTarget?.transfer_number}</strong> en <strong>{receiveTarget?.dest_warehouse_name}</strong>. El stock ingresará al depósito destino.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1 text-sm max-h-40 overflow-y-auto">
            {receiveTarget?.items?.map((it, i) => (
              <div key={i} className="flex justify-between bg-accent/30 rounded px-2 py-1">
                <span>{it.article_name}</span><span className="font-medium">{it.quantity} {it.unit}</span>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="received-by">Recibido por *</Label>
              <Input id="received-by" value={receiveBy} onChange={(e) => setReceiveBy(e.target.value)} placeholder="Nombre de quien recibe" data-testid="receive-by-input" />
            </div>
            <div>
              <Label htmlFor="received-date">Fecha de recepción *</Label>
              <Input id="received-date" type="date" max={todayLocalISO()} value={receiveDate} onChange={(e) => setReceiveDate(e.target.value)} data-testid="receive-date-input" />
            </div>
          </div>
          <div>
            <Label htmlFor="received-notes">Nota de recepción (opcional)</Label>
            <Textarea id="received-notes" rows={2} value={receiveNotes} onChange={(e) => setReceiveNotes(e.target.value)} placeholder="Ej: faltó 1 unidad, envase dañado, etc." data-testid="receive-notes-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceiveTarget(null)}>Cancelar</Button>
            <Button onClick={handleReceive} disabled={busy} data-testid="receive-confirm">{busy ? 'Recibiendo...' : 'Confirmar recepción'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancelar transferencia */}
      <Dialog open={!!cancelTarget} onOpenChange={(o) => { if (!o) { setCancelTarget(null); setCancelReason(''); } }}>
        <DialogContent data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600"><X className="w-5 h-5" /> Cancelar transferencia</DialogTitle>
            <DialogDescription>
              Se devolverá el stock de <strong>{cancelTarget?.transfer_number}</strong> al depósito origen ({cancelTarget?.source_warehouse_name}).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="cancel-reason">Motivo de la cancelación *</Label>
            <Textarea id="cancel-reason" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} rows={3} data-testid="cancel-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCancelTarget(null); setCancelReason(''); }}>Volver</Button>
            <Button variant="destructive" onClick={handleCancel} disabled={busy || cancelReason.trim().length < 5} data-testid="cancel-confirm">{busy ? 'Cancelando...' : 'Cancelar transferencia'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detalle */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent data-testid="transfer-detail-dialog">
          <DialogHeader>
            <DialogTitle>{detail?.transfer_number}</DialogTitle>
            <DialogDescription>{detail?.source_warehouse_name} → {detail?.dest_warehouse_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <Table>
              <TableHeader><TableRow><TableHead>Artículo</TableHead><TableHead>Lote</TableHead><TableHead className="text-right">Cant.</TableHead></TableRow></TableHeader>
              <TableBody>
                {(detail?.allocations || []).map((a, i) => (
                  <TableRow key={i}>
                    <TableCell>{a.article_name}</TableCell>
                    <TableCell className="font-mono text-xs">{a.lot_number || 's/lote'}</TableCell>
                    <TableCell className="text-right font-medium">{a.qty}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {detail?.notes && <p className="text-muted-foreground"><b>Obs.:</b> {detail.notes}</p>}
            {detail?.status === 'received' && (
              <p className="text-green-700"><b>Recibido por:</b> {detail.physically_received_by || detail.received_by_name} · <b>Fecha:</b> {detail.reception_date ? detail.reception_date.split('-').reverse().join('/') : fmtDateTime(detail.received_at)}{detail.reception_notes ? ` · Nota: ${detail.reception_notes}` : ''}</p>
            )}
            {detail?.status === 'cancelled' && <p className="text-red-600"><b>Cancelada:</b> {detail.cancellation_reason}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => downloadPdf(`/warehouse-transfers/${detail.transfer_id}/pdf`, detail.transfer_number)}><FileText className="w-4 h-4 mr-1" /> Remito</Button>
            <Button onClick={() => setDetail(null)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
